import React, { FunctionComponent } from 'react';
import Select, {
  components,
  OptionProps,
  SingleValueProps,
  PlaceholderProps,
  StylesConfig,
} from 'react-select';
import { DropdownProps, IData } from '../../types/dropdown';
import { CaretIcon, CheckIcon, XIcon } from '../icons';
import Typography from '../typography/Typography';
import './dropdown.scss';

/**
*  CUSTOM STYLES
*/

const defaultStyles: StylesConfig<any, false> = {
  control: (css, state) => ({
    ...css,
    height: '48px',
    padding: '0',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: 'none',
    border: state.isDisabled ? '1px solid #dfe2e5' : '1px solid #AFB6BE',
    overflowX: 'hidden',
  }),
  indicatorsContainer: (css) => ({
    ...css,
    position: 'absolute',
    right: '0',
    border: 'none',
    top: '50%',
    transform: 'translateY(-50%)',
    height: '30px',
    backgroundColor: 'white',
    paddingRight: '21px',
    paddingLeft: '17px',
  }),
  container: (css) => ({
    ...css,
    position: 'relative',
  }),
  menu: (css) => ({
    ...css,
    width: '100%',
    marginTop: 8,
    zIndex: 5,
    minWidth: 270,
    backgroundColor: 'white',
    position: 'absolute',
    top: '56px',
    padding: '16px',
    // height: props.height,
    maxHeight: 300,
    boxShadow:
      '0px 2px 4px rgba(97, 97, 97, 0.18), 0px 4px 8px rgba(97, 97, 97, 0.18)',
    borderRadius: 12,
  }),
  menuList: (css) => ({
    ...css,
    maxHeight: 200,
    paddingTop: 0,
    borderTop: '2px solid white',
    paddingRight: '10px',
    '::-webkit-scrollbar': {
      width: '4px',
      height: '0px',
    },
    '::-webkit-scrollbar-track': {
      background: '#fff',
    },
    '::-webkit-scrollbar-thumb': {
      background: '#C4C4C4',
      borderRadius: '8px',
    },
    '::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    },
  }),
  option: (css, state) => ({
    ...css,
    position: state.isMulti ? 'relative' : 'static',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '24px',
    padding: '10px 0 10px 10px',
    color: '#38414a',
    backgroundColor: state.isSelected || state.isFocused ? '#ebf6f9' : '#fff',
    '&:hover': {
      borderRadius: '8px',
    },
    cursor: 'pointer',
    borderRadius: state.isFocused || state.isSelected ? '8px' : 'none',
    '.option-remove-button': {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      right: '8px',
    },
    marginBottom: '8px',
  }),
  placeholder: (css, state) => ({
    ...css,
    color: state.isDisabled ? '#dfe2e5' : '#7e8a96',
  }),
  multiValue: (css) => ({
    ...css,
    backgroundColor: 'transparent',
    '.css-xb97g8': {
      display: 'none',
    },
  }),
  valueContainer: (css, state) => ({
    ...css,
    fontFamily: 'Rubik, sans-serif',
    clear: 'both',
    display: (state.isMulti && state.hasValue) ? 'inline-block' : 'flex',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  }),
};

/**
 *  CUSTOM DROPDOWN INDICATOR COMPONENT
 */

const DropdownIndicator: FunctionComponent = ({ selectProps }: any) => (
  <div data-testid={`dropdown-caretIcon_${selectProps.placeholder.replace(/\s/g, '-').toLowerCase()}`}>
    <CaretIcon width={10} direction="bottom" color="asphalt5" />
  </div>
);

const MultiValueContainer:FunctionComponent = ({ selectProps, data }: any) => {
  const values = selectProps.value;
  if (values) {
    return values[values.length - 1].label === data.label
      ? data.label
      : `${data.label}, `;
  } return '';
};

/**
 *  CUSTOM OPTION COMPONENT (OPTIONS IN THE MENU LIST)
 */

const Option: FunctionComponent<OptionProps> = (props) => {
  const data = props.data as IData;
  const isMulti = props.isMulti;
  const isSelected = props.isSelected;

  const renderIcon = () => {
    if (typeof data.icon === 'string') {
      return <span className={`icon-container  m-r-13 flag-icon flag-icon-${data.icon}`} />;
    }
    return <span className="icon-container  m-r-13 d-flex align-items-center">{data.icon}</span>;
  };

  return (
    <components.Option {...props}>
      <div className={`d-flex align-items-center ${isMulti ? 'multi' : ''}`}>
        {data.icon && renderIcon()}
        <span className="m-r-7 text-ellipsis">{data.label}</span>
        {isMulti && isSelected && (
          <XIcon color="red5" className="option-remove-button" />
        )}
      </div>
    </components.Option>
  );
};

/**
 *  CUSTOM SINGLE VALUE COMPONENT (CURRENT VALUE)
 */

const SingleValue: FunctionComponent<SingleValueProps> = (props) => {
  const data = props.data as IData;

  const renderIcon = () => {
    if (typeof data.icon === 'string') {
      return <span className={`icon-container m-r-13 flag-icon flag-icon-${data.icon}`} />;
    }
    return <span className="icon-container m-r-13 d-flex align-items-center">{data.icon}</span>;
  };

  return (
    <components.SingleValue {...props}>
      <div data-testid="dropdown-singleValue" className="d-flex align-items-center">
        {data.icon && renderIcon()}
        <Typography variant="body1" className="m-r-7 text-ellipsis">{data.label}</Typography>
      </div>
    </components.SingleValue>
  );
};

/**
 *  CUSTOM Placeholder COMPONENT
 */

const Placeholder: FunctionComponent<PlaceholderProps> = (props) => {
  const placeholder = props?.selectProps?.placeholder;
  return (
    <components.Placeholder {...props}>
      <div data-testid="dropdown-placeholderValue" className="d-flex align-items-center">
        {placeholder}
      </div>
    </components.Placeholder>
  );
};

/**
 *  DROPDOWN COMPONENT
 */

const Dropdown: FunctionComponent<DropdownProps> = ({
  data,
  value,
  onChange,
  onBlur,
  label,
  disabled,
  isSearchable = false,
  placeholder,
  className = '',
  confirmed = false,
  defaultValue,
  isMulti,
  styles,
  fullWidth,
  closeMenuOnSelect,
}) => (
  <div className={`dropdown-component ${className} ${confirmed && 'success'} ${fullWidth && 'w-12'}`}>
    <Typography
      variant="caption"
      className="dropdown-label"
    >
      {label}
    </Typography>

    <Select
      components={{
        DropdownIndicator,
        IndicatorSeparator: null,
        Option,
        SingleValue,
        MultiValueContainer,
        Placeholder,
      }}
      options={data}
      className="select-dropdown"
      isMulti={isMulti}
      {...onChange && { onChange }}
      onBlur={onBlur}
      {...(value !== undefined && { value: data.find((option: IData) => option.value === value) || null })}
      placeholder={placeholder}
      styles={styles || defaultStyles}
      isDisabled={disabled}
      isSearchable={isSearchable}
      defaultValue={data.filter((option: any) => defaultValue === option.value)}
      autoFocus
      backspaceRemovesValue={false}
      hideSelectedOptions={false}
      isClearable={false}
      tabSelectsValue={false}
      menuPlacement="auto"
      closeMenuOnSelect={closeMenuOnSelect}
    />

    {confirmed && (
    <CheckIcon
      width={17}
      color="atlantis5"
      className="check-icon"
    />
    )}
  </div>
);

export default Dropdown;
